import { useMemo } from "react";
import { useCan } from "../../../authz/useCan";
import type { DashboardPermissions } from "../types/dashboard.types";

export const useDashboardPermissions = (): DashboardPermissions => {
  const canAppsCrud = useCan("school.applications", "crud");
  const canAppsManage = useCan("school.applications", "manage");
  const canManageApplications = canAppsCrud || canAppsManage;

  const canMutCrud = useCan("school.mutations", "crud");
  const canMutManage = useCan("school.mutations", "manage");
  const canManageMutations = canMutCrud || canMutManage;

  const canListWaiting = useCan("school.waiting-list", "list");
  const canViewWaitingGrant = useCan("school.waiting-list", "view");
  const canAssignWaiting = useCan("school.waiting-list", "assign");
  const canViewWaitingList = canListWaiting || canViewWaitingGrant || canAssignWaiting;

  const canViewReportsRoot = useCan("reports.root", "view");
  const canViewUnassigned = useCan("signals.unassigned-children", "view");
  const canViewWaitingWithClass = useCan("signals.waiting-list-conflicts", "view");
  const canViewCurrentSchoolYearSignals = useCan("signals.current-school-year", "view");
  const canViewPrivacyReports = useCan("reports.privacy", "view");
  const canViewStudentReports = useCan("reports.students", "view");
  const canViewAttendanceReports = useCan("reports.attendance", "view");
  const canViewOutingsReport = useCan("reports.outings", "view");
  const canReadAlumni = useCan("alumni", "read");
  const canCrudAlumni = useCan("alumni", "crud");
  const canSeeAlumni = canReadAlumni || canCrudAlumni;
  const canViewAnalyticsReportsPermission = useCan("admin.analytics", "read");

  const canTeachersList = useCan("school.teachers", "list");
  const canTeachersRead = useCan("school.teachers", "read");
  const canTeachersCrud = useCan("school.teachers", "crud");
  const canTeachersManage = useCan("school.teachers", "manage");
  const canViewTeachers = canTeachersList || canTeachersRead || canTeachersCrud || canTeachersManage;

  const canYearsList = useCan("school.years", "list");
  const canYearsView = useCan("school.years", "view");
  const canYearsRead = useCan("school.years", "read");
  const canYearsCrud = useCan("school.years", "crud");
  const canYearsManage = useCan("school.years", "manage");
  const canViewYears = canYearsList || canYearsView || canYearsRead || canYearsCrud || canYearsManage;

  const canViewClassrooms = useCan("school.classrooms", "view");
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canManageClassrooms = useCan("school.classrooms", "manage");
  const canReadSchoolCalendar = useCan("school.calendar", "read");
  const canManageSchoolCalendar = useCan("school.calendar", "manage");
  const canSeeSchoolCalendar = canReadSchoolCalendar || canManageSchoolCalendar;
  const attendanceFeatureEnabled = useCan("school.attendance", "view");

  const canSeeAdminRoot = useCan("admin", "view");
  const canViewAdminUsers = useCan("admin.users", "manage");
  const canViewAdminLogs = useCan("admin.audit", "read");
  const canManageBackupsPermission = useCan("admin.backups", "manage");
  const canNotificationsManage = useCan("admin.notifications", "manage");
  const canNotificationsSend = useCan("admin.notifications", "send");
  const canManageEmailValidation = useCan("email.validation", "manage");
  const canSearchFamilyBankAccounts = useCan("families.bankaccounts", "search");
  const canSeeDirectory = useCan("directory", "manage");

  const canSeeGiteaIssues = useCan("integration.gitea.issues", "read");
  const canSeeNoctis = useCan("integration.noctis.object-types", "view");
  const canSeeEBoekhouden = useCan("finance.settings", "view");
  const canSeeFinanceRoot = useCan("finance.root", "view");
  const canViewFinanceContributions = useCan("finance.contributions", "view");
  const canSeeMailchimpStatus = useCan("newsletter.recipients", "status");
  const canNewsletterExport = useCan("newsletter.recipients", "export");
  const canNewsletterSync = useCan("newsletter.recipients", "sync");
  const canSeeMailchimp = canSeeMailchimpStatus || (canNewsletterExport && canNewsletterSync);

  const canViewMyEnvironment = useCan("my.environment", "view");
  const canViewMyClassrooms = useCan("my.classrooms", "view");
  const canViewMyParentMeetings = useCan("my.parent-meetings", "view");
  const canViewMyAgendaActivities = useCan("my.agenda-activities", "view");
  const canReadClassroomCalendar = useCan("classroom.calendar", "read");
  const canManageClassroomCalendar = useCan("classroom.calendar", "manage");
  const canManageOutingInfo = useCan("outing.info", "manage");
  const canReadOutingInfo = useCan("outing.info", "read");

  const canViewPlacementOverview = useCan("school.placement-overview", "view");
  const canManageOverview = useCan("school.classrooms.overview", "manage");
  const canViewFuturePlanning = useCan("school.future-planning", "view");
  const canManageFuturePlanning = useCan("school.future-planning", "manage");
  const canReadSchoolRoot = useCan("school", "read");
  const canManageSchoolRoot = useCan("school", "manage");

  return useMemo(() => {
    const canViewMyAgendaActivitiesPage =
      canViewMyAgendaActivities ||
      canReadClassroomCalendar ||
      canManageClassroomCalendar ||
      canReadOutingInfo ||
      canManageOutingInfo ||
      canViewOutingsReport;
    const canSeeMyEnvironment = canViewMyEnvironment || canViewMyClassrooms || canViewMyParentMeetings || canViewMyAgendaActivitiesPage;
    const canSeeApplications = canManageApplications;
    const canSeeClassrooms = canViewClassrooms || canCrudClassrooms || canManageClassrooms;
    const canSeeClassroomsOverview = canManageOverview || canCrudClassrooms || canManageClassrooms;
    const canSeeFuturePlanning = canViewFuturePlanning || canManageFuturePlanning;
    const canSeePlacementSection =
      canViewPlacementOverview || canSeeApplications || canViewWaitingList || canSeeClassroomsOverview || canSeeFuturePlanning;
    const canSeeSchoolRoot = canReadSchoolRoot || canManageSchoolRoot;
    const canSeeSchoolSection = canSeeSchoolRoot;
    const canSeeSignalsSection = canViewUnassigned || canViewWaitingWithClass || canViewCurrentSchoolYearSignals;
    const canViewAnalyticsReports = canViewReportsRoot && canViewAnalyticsReportsPermission;
    const canSeeReportsSection =
      canViewReportsRoot ||
      canViewPrivacyReports ||
      canViewStudentReports ||
      (attendanceFeatureEnabled && canViewAttendanceReports) ||
      canViewOutingsReport ||
      canViewAnalyticsReports;
    const canSeeToolsSection = canSeeDirectory || canSearchFamilyBankAccounts;
    // Same checks as the /finance route, so the tile never leads to "no access".
    const canSeeFinanceSection = canSeeFinanceRoot || canSeeEBoekhouden || canViewFinanceContributions || canSearchFamilyBankAccounts;
    const canSeeAdministrationSection = canManageMutations || canSeeAlumni;
    const canSeeNotificationsAdmin = canNotificationsManage || canNotificationsSend;
    const canManageBackups = canManageBackupsPermission;
    const canSeeMaintenance = canManageOutingInfo || canSeeMailchimp || canManageEmailValidation;
    const canSeeManagementSection =
      canSeeAdminRoot || canViewAdminUsers || canSeeNotificationsAdmin || canManageBackups || canSeeMaintenance || canViewAdminLogs;
    const canSeeIntegrationsSection = canSeeMailchimp || canSeeNoctis || canSeeGiteaIssues || canSeeEBoekhouden;

    return {
      canSeeMyEnvironment,
      canSeePlacementSection,
      canSeeSchoolSection,
      canSeeSignalsSection,
      canSeeReportsSection,
      canSeeFinanceSection,
      canSeeToolsSection,
      canSeeAdministrationSection,
      canSeeManagementSection,
      canSeeIntegrationsSection,
      canManageApplications,
      canManageMutations,
      canViewWaitingList,
      canSeeGiteaIssues,
      canSeeNoctis,
      canSeeMailchimp,
      canSeeEBoekhouden,
      canViewMyClassrooms,
      canViewMyParentMeetings,
      canViewMyAgendaActivitiesPage,
      canLoadPersonalCalendar: canSeeMyEnvironment && (canReadClassroomCalendar || canManageClassroomCalendar),
      canViewPlacementOverview,
      canSeeApplications,
      canSeeFuturePlanning,
      canSeeClassroomsOverview,
      canSeeAdminRoot,
      canSeeNotificationsAdmin,
      canManageBackups,
      canViewAdminLogs,
      canViewReportsRoot,
      canViewPrivacyReports,
      canViewStudentReports,
      canViewAttendanceReports,
      canViewOutingsReport,
      canViewAnalyticsReports,
      attendanceFeatureEnabled,
    };
  }, [
    attendanceFeatureEnabled,
    canCrudClassrooms,
    canManageApplications,
    canManageBackupsPermission,
    canManageClassroomCalendar,
    canManageClassrooms,
    canManageEmailValidation,
    canManageFuturePlanning,
    canManageMutations,
    canManageOutingInfo,
    canManageOverview,
    canManageSchoolRoot,
    canNotificationsManage,
    canNotificationsSend,
    canReadClassroomCalendar,
    canReadOutingInfo,
    canSearchFamilyBankAccounts,
    canSeeAdminRoot,
    canSeeDirectory,
    canSeeGiteaIssues,
    canSeeMailchimp,
    canSeeNoctis,
    canSeeEBoekhouden,
    canSeeFinanceRoot,
    canViewFinanceContributions,
    canViewAdminLogs,
    canViewAdminUsers,
    canViewAnalyticsReportsPermission,
    canViewAttendanceReports,
    canViewClassrooms,
    canViewFuturePlanning,
    canViewMyAgendaActivities,
    canViewMyClassrooms,
    canViewMyEnvironment,
    canViewMyParentMeetings,
    canViewOutingsReport,
    canViewPlacementOverview,
    canViewPrivacyReports,
    canViewReportsRoot,
    canViewStudentReports,
    canViewTeachers,
    canViewUnassigned,
    canViewWaitingList,
    canViewWaitingWithClass,
    canViewCurrentSchoolYearSignals,
    canViewYears,
    canReadSchoolRoot,
    canSeeAlumni,
    canSeeSchoolCalendar,
  ]);
};
